import React, {useState} from "react";
import {
    Box,
    Button,
    HStack,
    Input,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast,
    VStack
} from "@chakra-ui/react";
import {AddIcon} from "@chakra-ui/icons";
import {useMutation, useQuery} from "@tanstack/react-query";
import {FaTrash} from "react-icons/fa";
import axios from "axios";
import {BASE_URL_BRIDGE, IS_SMARTVENUES} from "../../../../utils/Utils";
import {useDialogStore} from "../../../../store/useAlertDialog";

export const RagDocumentsPage: React.FC = () => {
    const toast = useToast();

    const {data, isLoading, refetch} = useQuery({
        queryKey: ["rag-documents"],
        queryFn: getRagDocuments,
        select: (res) => res.data
    });

    const deleteMutation = useMutation({
        mutationFn: deleteRagDocument,
        onSuccess: () => {
            toast({title: "Documento eliminato", status: "success"});
            refetch();
        },
        onError: () => toast({title: "Errore eliminazione", status: "error"})
    });

    const {showDialog, closeDialog} = useDialogStore();

    return (
        <VStack w="full" h="full" align="flex-start" spacing={6} p={6}>
            <HStack w="full" justify="space-between">
                <Text fontSize="2xl" fontWeight="bold">Documenti</Text>
            </HStack>

            <HStack
                cursor="pointer"
                h="64px"
                px="24px"
                borderRadius="24px"
                alignItems="center"
                justifyContent="center"
                bottom="16px"
                right="16px"
                backgroundColor="secondary.200"
                _hover={{backgroundColor: "tertiary.200"}}
                onClick={() => {
                    // navigate("/events/add");
                    showDialog({
                        title: "Aggiungi documento",
                        description: "Scegli un file dal computer per effettuare l'upload.\nNel campo di testo è possibile scrivere delle parole chiave da associare al testo, ad esempio il nome del prodotto se si tratta di una scheda tecnica, o più nomi prodotto separati da un trattino (-).",
                        children: <AddDocumentDialog doRefetch={refetch} closeDialog={closeDialog}/>
                    })
                }}
            >
                <AddIcon w="16px" h="16px"/>
                <Text ms="8px" fontWeight="bold">
                    Aggiungi
                </Text>
            </HStack>

            <Box w="full" overflowX="auto" bg="white" borderRadius="lg" shadow="sm">
                <Table variant="simple" minW="600px">
                    <Thead>
                        <Tr>
                            <Th>Nome File</Th>
                            <Th>Data Caricamento</Th>
                            <Th>Prodotti</Th>
                            <Th>Azioni</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading ? (
                            <Tr>
                                <Td colSpan={3} textAlign="center">
                                    <Spinner size="sm"/>
                                </Td>
                            </Tr>
                        ) : data?.length > 0 ? (
                            data.map((doc: any) => (
                                <Tr key={doc.id}>
                                    <Td maxW="300px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                        {doc.filename}
                                    </Td>
                                    <Td>{new Date(doc.created_at).toLocaleString()}</Td>
                                    <Td>{doc.key_words}</Td>
                                    <Td>
                                        <Button
                                            size="sm"
                                            leftIcon={<FaTrash/>}
                                            onClick={() => showDialog({
                                                title: "Elimina ordine",
                                                description: "Sicuro di volere procedere con l'eliminazione?",
                                                cancelButtonText: "Annulla",
                                                confirmButtonText: "Elimina",
                                                onPressCancelButton: () => closeDialog(),
                                                onConfirmButtonClick: () => {
                                                    deleteMutation.mutateAsync(doc.id).then(() => {
                                                        closeDialog();
                                                    });
                                                }
                                            })}
                                            colorScheme="red"
                                        >
                                            Elimina
                                        </Button>
                                    </Td>
                                </Tr>
                            ))
                        ) : (
                            <Tr>
                                <Td colSpan={3} textAlign="center">
                                    Nessun documento caricato.
                                </Td>
                            </Tr>
                        )}
                    </Tbody>
                </Table>
            </Box>
        </VStack>
    );
};

// export const createRag = () => {
//     return axios.post(`${BASE_URL_BRIDGE}/create_rag`);
// };

export const uploadRagPdf = (file: File, productName?: string) => {
    const userType = localStorage.getItem("userType");
    const producerId = localStorage.getItem("producerId");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", IS_SMARTVENUES ? "smartvenues" : "enoteca");
    formData.append("query", "Upload file");

    if (productName && productName.trim() !== "") {
        formData.append("product_name", productName);
    }

    if (producerId) {
        formData.append("producer_id", producerId);
    }
    if (userType) {
        formData.append("user_type", userType);
    }

    return axios.post(`${BASE_URL_BRIDGE}/upload_pdf`, formData);
};

const AddDocumentDialog = ({doRefetch, closeDialog}: { doRefetch: () => void, closeDialog: () => void }) => {
    const [productName, setProductName] = useState<string | undefined>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const uploadMutation = useMutation({
        mutationFn: async ({file, productName}: { file: File, productName?: string }) => {
            return uploadRagPdf(file, productName);
        },
        onSuccess: () => {
            setSelectedFile(null);
            doRefetch();
            closeDialog();
        },
        onError: (error) => {
            console.error("Errore durante l'upload:", error);
        }
    });

    return <VStack w={"100%"} alignItems={'flex-start'}>
        <Text fontWeight={"bold"} h={"100%"} verticalAlign={"start"}
              textAlign={"start"}>Documento (*)</Text>
        <Input
            w={'100%'}
            type="file"
            title={"Prodotti associati"}
            accept="application/pdf"
            p={'4px'}
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            flex={1}
        />

        <Text fontWeight={"bold"} h={"100%"} verticalAlign={"start"}
              textAlign={"start"} mt={'8px'}>Nome prodotto (facoltativo)</Text>
        <Input
            inputMode={"text"}
            colorScheme={"primary"}
            variant='filled'
            _placeholder={{fontSize: 14}}
            errorBorderColor={"error.700"}
            // disabled={isSubmitting}
            value={productName}
            onChange={(e) => {
                setProductName(e?.target?.value);
            }}
            placeholder='Es. Il mio prodotto'/>

        <HStack w={'100%'} gap={2} mt={"24px"}>
            <Button onClick={() => {
                closeDialog();
            }} flex={1} cursor={'pointer'} color={'primary.400'}>
                Annulla
            </Button>
            <Button
                onClick={() => {
                    if (selectedFile) uploadMutation.mutate({file: selectedFile, productName});
                }}
                disabled={selectedFile === null || uploadMutation.isPending}
                flex={1}
                cursor={'pointer'}
                bg={'primary.400'}
                color={'white'}
                _hover={{bg: 'primary.500'}}
                _active={{bg: 'primary.500'}}
            >
                {uploadMutation.isPending ? <Spinner size="md"/> : "Carica"}
            </Button>
        </HStack>
    </VStack>
}

/*export const uploadRagPdf = (file: File, ragId: string) => {
    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("rag_id", ragId);

    return axios.post(`${BASE_URL_BRIDGE}/upload_pdf`, formData);
};*/

export const getRagDocuments = () => {
    const producerId = localStorage.getItem("producerId");
    const userType = localStorage.getItem("userType") || "user"; // default non-admin

    return axios.get(`${BASE_URL_BRIDGE}/rag/list`, {
        params: {
            type: `${IS_SMARTVENUES ? 'smartvenues' : 'enoteca'}`,
            user_type: userType,
            producer_id: producerId || undefined,
        }
    });
};

export const deleteRagDocument = (id: string) => {
    const producerId = localStorage.getItem("producerId");
    const userType = localStorage.getItem("userType") || "user"; // default non-admin

    return axios.delete(`${BASE_URL_BRIDGE}/delete_doc/${id}`, {
        params: {
            type: IS_SMARTVENUES ? "smartvenues" : "enoteca",
            user_type: userType,
            producer_id: producerId || undefined,
        },
    });
};
